import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { DeckCanvas } from 'components'
import { DeckMode } from 'common/deckMode'
import MaintenanceSummaryForm from '../components/SummaryForm'
import { addMaintenanceAction, addWearAction, setHistoryWithForm } from 'pages/common/maintenance/logic'
import BigNumber from 'bignumber.js'
import { useMaintenance } from 'hooks'
import { useCurrentDeckContext } from 'components/currentDeckContext/CurrentDeckContext'
import { MobileDeckCanvasAndMaintenanceForm } from 'pages/deck/maintenance/mobile/MobileDeckCanvasAndMaintenanceForm'
import { ProgressIndicator } from 'components/maintenanceSelector/wearapp/components/ProgressIndicator'
import { use2D3DViewSwitch } from 'hooks/use2D3DViewSwitch'
import withPropsChecker from 'components/withWhatPropChanged'
import { useOnBomClick } from 'hooks/useOnBomClick'
import { DeckLayoutContainer } from 'pages/deck/common/DeckLayoutContainer'
import BomTable from 'components/tables/bom/BomTable'
import { DeckAccordion } from 'pages/deck/common/DeckAccordion'
import { Box } from '@mui/material'
import { OtherKitsMaintenanceTable } from './OtherKitsMaintenanceTable'
import { useScrollToOtherKits } from './ScrollContext'

const DeckCanvasAndMaintenanceFormComponent = () => {
  const { actions, getAction } = useMaintenance()
  const [awaitingActionId, setAwaitingActionId] = useState(-1)
  const otherKitsTableRef = useRef(null)
  const { registerScrollToOtherKits } = useScrollToOtherKits()
  const [mouseCanvasPosition, setMouseCanvasPosition] = useState(
    JSON.parse(localStorage.getItem('3dMouseCanvasPosition') ?? '{"x":0,"y":0}')
  )

  const [accordionState, setAccordionState] = useState({
    top: true,
    bottom: true
  })

  // Register the scroll function with the context
  useEffect(() => {
    const scrollFn = () => {
      if (otherKitsTableRef.current) {
        otherKitsTableRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        })
      }
    }
    registerScrollToOtherKits(scrollFn)
  }, [registerScrollToOtherKits])

  useEffect(() => {
    localStorage.setItem('3dMouseCanvasPosition', JSON.stringify(mouseCanvasPosition))
  }, [mouseCanvasPosition])

  const { materialSelected, onBomClick, setMaterialSelected } = useOnBomClick(() => {
    setSelectedPanel([])
  })
  const {
    history,
    setSelectedPanel,
    selectedPanel,
    database,
    maintenanceData,
    setMaintenanceData,
    inMaintenanceMode,
    maintenanceFrequency,
    deckId
  } = useCurrentDeckContext()

  const [viewType] = use2D3DViewSwitch()

  const onUndo = useCallback(() => {
    const data = history.undoHistory()
    if (data) {
      setMaintenanceData(data)
    }
  }, [history.undoHistory])

  const onRedo = useCallback(() => {
    const data = history.redoHistory()
    if (data) {
      setMaintenanceData(data)
    }
  }, [history.redoHistory])

  const onPanelClick = useCallback(
    (data, e) => {
      if (data) {
        if (data !== 'cancel') {
          if (new BigNumber(awaitingActionId).isGreaterThanOrEqualTo(0)) {
            const result = addMaintenanceAction(
              getAction(awaitingActionId),
              awaitingActionId,
              Array.isArray(data) ? data : [data],
              maintenanceData,
              actions
            )

            setMaintenanceData(result.clone)
            history.addHistory(result.clone)
            setAwaitingActionId(result.actionId)
            setSelectedPanel([])
          } else if (e) {
            const position = { x: e.layerX, y: e.layerY }
            setMouseCanvasPosition(position)
          }
        } else {
          setSelectedPanel([])
        }
      }
      setMaterialSelected('')
    },
    [awaitingActionId, maintenanceData, history.addHistory]
  )

  const onSelectChange = useCallback(
    (data, e) => {
      if (new BigNumber(awaitingActionId).isLessThan(-1)) {
        setSelectedPanel([])
        setMaterialSelected('')
        setAwaitingActionId(-1)
      } else {
        setSelectedPanel(data)
        setMaterialSelected('')
      }

      if (e) {
        const position = { x: e.layerX, y: e.layerY }
        setMouseCanvasPosition(position)
      }
    },
    [awaitingActionId]
  )

  const cancelAwaitingAction = useCallback(() => {
    if (new BigNumber(awaitingActionId).isGreaterThanOrEqualTo(0)) {
      let clone = structuredClone(maintenanceData)
      clone.deckRevisionHistory.Details.pop()
      setMaintenanceData(clone)
      setAwaitingActionId(-1)
    }
  }, [awaitingActionId, maintenanceData])

  //various submissions that update on the fly, such as WearApp, the SummaryForm, adding an Action, etc.
  function onFormChange(form) {
    const clone = setHistoryWithForm(form, maintenanceData)
    history.addHistory(clone)
    setMaintenanceData(clone)
  }

  const onMaintenanceTypeClick = useCallback(
    (action, extraData) => {
      const result = addMaintenanceAction(action, awaitingActionId, selectedPanel, maintenanceData, actions, extraData)
      setMaintenanceData(result.clone)
      if (result.actionId < 0) {
        history.addHistory(result.clone)
      }
      setAwaitingActionId(result.actionId)
      setSelectedPanel([])
    },
    [awaitingActionId, selectedPanel, maintenanceData, actions, history.addHistory]
  )

  const onWearSubmit = useCallback(
    (form) => {
      const clone = addWearAction(form, maintenanceData, selectedPanel, actions.NoChange.id)
      setMaintenanceData(clone)
      history.addHistory(clone)
      setSelectedPanel([])
    },
    [maintenanceData, selectedPanel, history.addHistory]
  )

  const deckRevision = useMemo(() => {
    const revisions = database.deckRevisions
      .filter((d) => d.DeckId == deckId && d.IsPublished && !d.IsArchived)
      .sort((a, b) => b.RevisionNumber - a.RevisionNumber)
    if (revisions.length) {
      const revision = structuredClone(revisions[0])
      return revision
    }
  }, [deckId, database.deckRevisions])

  // const switchViewType = useCallback((e) => {
  //   setViewType((currentViewType) => (currentViewType === '3D' ? '2D' : '3D'))
  //   e.stopPropagation()
  // }, [])

  const hasFoundMaintenanceFreq = typeof maintenanceFrequency === 'undefined'

  const getTopHeight = () => {
    if (accordionState.bottom && accordionState.top) {
      return '50%'
    } else if (accordionState.top) {
      return '80%'
    }
    return '20%'
  }

  const getBottomHeight = () => {
    if (accordionState.bottom && accordionState.top) {
      return '50%'
    } else if (accordionState.bottom) {
      return '80%'
    }
    return '20%'
  }

  return (
    <DeckLayoutContainer
      canToggle
      deckRevision={deckRevision}
      Deck={
        <>
          {viewType === '3D' ? (
            <DeckCanvas
              data={deckRevision}
              maintenance={maintenanceData.deckRevisionHistory}
              mouseCanvasPosition={mouseCanvasPosition}
              onPanelClick={onPanelClick}
              onMaintenanceTypeClick={onMaintenanceTypeClick}
              selected={selectedPanel}
              materialSelected={materialSelected}
              onSelectChange={onSelectChange}
              onWearSubmit={onWearSubmit}
              awaitingActionId={awaitingActionId}
              cancelAwaitingAction={cancelAwaitingAction}
              mode={inMaintenanceMode ? DeckMode.maintenance : DeckMode.default}
              onUndo={onUndo}
              onRedo={onRedo}
              isHistoryEmpty={history.isEmpty}
              isHistoryAtEnd={history.isAtEnd}
            />
          ) : (
            <>
              <MobileDeckCanvasAndMaintenanceForm
                twoDonDekstop
                materialSelected={materialSelected}
                onUndo={onUndo}
                onRedo={onRedo}
                isHistoryEmpty={history.isEmpty}
                isHistoryAtEnd={history.isAtEnd}
              />
            </>
          )}
        </>
      }
      topRightElement={
        <Box>
          <Box mb={1}>
            {hasFoundMaintenanceFreq ? (
              <ProgressIndicator />
            ) : (
              <DeckAccordion
                showTooltip={false}
                title="Maintence summary"
                onChange={(val) => {
                  setAccordionState((prev) => {
                    return {
                      ...prev,
                      top: val
                    }
                  })
                }}>
                <MaintenanceSummaryForm
                  data={maintenanceData}
                  deckRevision={deckRevision}
                  selected={selectedPanel}
                  materialSelected={materialSelected}
                  onBomClick={onBomClick}
                  onChange={onFormChange}
                  isMobile={false}
                  inMaintenanceMode={inMaintenanceMode}
                  hideTitle
                  fullHeight
                />
              </DeckAccordion>
            )}
          </Box>
          <OtherKitsMaintenanceTable
            ref={otherKitsTableRef}
            deckRevision={deckRevision}
            otherKitsMaintenance={maintenanceData?.deckRevisionHistory?.OtherKitsMaintenance}
          />
          <BomTable
            fullHeight
            data={deckRevision}
            selected={selectedPanel}
            materialSelected={materialSelected}
            onClick={onBomClick}
            onChange={(val) => {
              setAccordionState((prev) => {
                return {
                  ...prev,
                  bottom: val
                }
              })
            }}
          />
        </Box>
      }
      topRightSx={{
        height: getTopHeight()
      }}
      bottomRightSx={{
        height: getBottomHeight()
      }}
    />
  )
}

export const DeckCanvasAndMaintenanceForm = withPropsChecker(DeckCanvasAndMaintenanceFormComponent, 'DeckAndCanvasForm')
